/**
 * Every browser in a huddle connects to every other (a mesh), so each one uploads its audio once
 * per listener. Past this, a media server is the way to grow, not a bigger number here.
 */
export const HUDDLE_MAX_PARTICIPANTS = 6

/** How often a browser in a huddle says it is still there. */
export const HUDDLE_HEARTBEAT_MS = 15 * 1000

/** No heartbeat for this long and the seat is given up: a crashed tab, a lost network. */
export const HUDDLE_STALE_MS = 45 * 1000

/** Public STUN, enough for most networks. A TURN relay for strict firewalls comes from config. */
export const DEFAULT_HUDDLE_ICE_SERVERS: IHuddleIceServer[] = [
  { urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] },
]

export enum HuddleStatus {
  Live = 'live',
  Ended = 'ended',
}

/** RTCIceServer, restated so the server needs no DOM types. */
export interface IHuddleIceServer {
  urls: string | string[]
  username?: string
  credential?: string
}

/** One browser in the huddle. A person in two tabs would be two peers, so a join is per tab. */
export interface IHuddlePeer {
  peerId: string
  userId: string
  name: string
  avatar: string | null
  isMuted: boolean
  joinedAt: string
}

export interface IHuddle {
  id: string
  channelId: string
  status: HuddleStatus
  startedByUserId: string
  startedAt: string
  endedAt: string | null
  /** The System row in the conversation that keeps the huddle's record. */
  messageId: string | null
  peers: IHuddlePeer[]
  /** The person whose browser carries Arc into the room, while Arc is in it. */
  arcHostUserId: string | null
}

/** What joining hands back: the room, this tab's seat in it, and how to reach the others. */
export interface IHuddleJoin {
  huddle: IHuddle
  peerId: string
  iceServers: IHuddleIceServer[]
}

/** The huddle as its row in the conversation keeps it, for the timeline and after it ends. */
export interface IHuddleRecord {
  huddleId: string
  status: HuddleStatus
  startedByUserId: string
  startedAt: string
  endedAt: string | null
  /** Wall-clock length once ended. */
  durationMs: number | null
  /** Everyone who was in it at any point, in the order they first joined. Names as of then. */
  participants: { userId: string; name: string }[]
  /** Who is in it right now. Empty once ended. */
  activeUserIds: string[]
  /** Arc was brought in at some point. */
  hadArc: boolean
  /** Written a little after it ends, from the transcript. */
  summary?: IHuddleSummary | null
}

export enum HuddleSignalKind {
  Offer = 'offer',
  Answer = 'answer',
  Candidate = 'candidate',
}

/** One WebRTC negotiation step, from one peer to another, relayed over the app socket. */
export interface IHuddleSignal {
  huddleId: string
  fromPeerId: string
  toPeerId: string
  kind: HuddleSignalKind
  /** An SDP for an offer or answer; a serialized RTCIceCandidateInit for a candidate. */
  payload: string
}

/** A relayed signal as it arrives, with who sent it stamped by the server. */
export interface IHuddleSignalDelivery extends IHuddleSignal {
  fromUserId: string
}

/** Bounds on a relayed signal: an SDP for audio only is a few KB. */
export const HUDDLE_SIGNAL_MAX_CHARS = 20_000

/** What Arc's insight check reads: the room's latest lines, and what Arc itself said lately. */
export const HUDDLE_INSIGHT_MAX_LINES = 40
export const HUDDLE_INSIGHT_LINE_MAX_CHARS = 500

export enum HuddleSpeaker {
  Person = 'person',
  Arc = 'arc',
}

/** Each browser records its own mic in stretches this long, each transcribed on its own. */
export const HUDDLE_AUDIO_CHUNK_MS = 30 * 1000
/** A stretch still finishing when its seat was given up is accepted this long after. */
export const HUDDLE_LATE_AUDIO_MS = 2 * 60 * 1000
export const HUDDLE_ARC_LINE_MAX_CHARS = 4000

export interface IHuddleTranscriptLine {
  id: string
  speaker: HuddleSpeaker
  userId: string
  /** The person's name, or "Arc". */
  name: string
  text: string
  spokenAt: string
}

export interface IHuddleTranscript {
  huddleId: string
  /** The conversation it was in, so a task made from its summary can link back. */
  channelId: string
  summary: IHuddleSummary | null
  status: HuddleStatus
  startedAt: string
  endedAt: string | null
  lines: IHuddleTranscriptLine[]
}

/** Asking colleagues into a huddle you're in: a toast on their screens with a Join button. */
export interface IHuddleInvite {
  channelId: string
  huddleId: string
  fromUserId: string
  fromName: string
  /** `#name` for a channel; null for a DM or group, where who's asking says enough. */
  channelLabel: string | null
}

/** Whether one person is in a huddle, for the headphones beside their name. */
export interface IHuddlePresence {
  userId: string
  isInHuddle: boolean
}

/** One thing to do that came out of a huddle; `taskId` once someone made it a task. */
export interface IHuddleSummaryTask {
  title: string
  ownerUserId: string | null
  ownerName: string | null
  taskId: string | null
}

/** What a huddle came to, written from its transcript once it ends. */
export interface IHuddleSummary {
  overview: string
  takeaways: string[]
  tasks: IHuddleSummaryTask[]
  generatedAt: string
}

/** A huddle in a person's or a group's history. */
export type IHuddleHistoryItem = Omit<
  IHuddleRecord,
  'startedByUserId' | 'activeUserIds' | 'summary'
> & {
  channelId: string
  /** The conversation's name, or null for a DM or an unnamed group. */
  channelName: string | null
  summary: IHuddleSummary | null
  lineCount: number
}

/** A summary isn't written for a huddle with fewer transcript lines than this. */
export const HUDDLE_SUMMARY_MIN_LINES = 4

/** Was it you? Sent to the person Arc's host says just spoke to Arc. */
export interface IHuddleSpeakerCheck {
  checkId: string
  channelId: string
  huddleId: string
  /** When the line was said, as ages at the moment this was sent: the browser checks its own mic over that span. */
  fromAgoMs: number
  toAgoMs: number
}

/** The longest a line said to Arc is looked back over. */
export const HUDDLE_SPEAKER_WINDOW_MAX_MS = 8 * 1000
/** A line older than this by the time Arc hands it off isn't vouched for: it runs as the host. */
export const HUDDLE_SPEAKER_MAX_AGE_MS = 12 * 1000

/** One recorded stretch of a browser's own voice, posted for the huddle transcript. */
export interface IHuddleAudioChunk {
  file: File
  /** When the stretch started, so its lines land in the right place in the transcript. */
  startedAt: string
}
